import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  AlertTriangle,
  CalendarClock,
  ChevronLeft,
  CreditCard,
  FileText,
  History,
  Menu,
  Package,
  PackageCheck,
  ServerCog,
  Settings,
  ShieldCheck,
  ShoppingCart,
  Ticket,
  Truck,
  Inbox,
  PlugZap,
  UsersRound,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { isExamShipmentDistributionEnabled } from "@/features/examShipmentDistributionFeature";
import { isIntegratedOrdersEnabled } from "@/features/integratedOrdersFeature";
import { isInvoiceReturnsEnabled } from "@/features/invoiceReturnsFeature";
import { isCustomerGroupAddressEnabled } from "@/features/customerGroupAddressFeature";
import { isNewOrderZendeskTicketEnabled } from "@/features/newOrderZendeskTicketFeature";
import { isNewScheduledOrderCycleEnabled } from "@/features/newScheduledOrderCycleFeature";
import { canOverrideDynamicsEnvironment } from "@/lib/dynamicsEnvironment";
import { cn } from "@/lib/utils";

interface LayoutProps {
  children: React.ReactNode;
  title: string;
}

export function Layout({ children, title }: LayoutProps) {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const administrativeItems = [
    { path: "/admin-pedidos", label: "Admin Pedidos", icon: AlertTriangle },
    { path: "/historico-pedidos", label: "Histórico", icon: History },
    { path: "/catalogo-produtos", label: "Catálogo", icon: Package },
    { path: "/reajuste", label: "Reajuste", icon: ShoppingCart },
    { path: "/faturamentos", label: "Faturamentos", icon: FileText },
    { path: "/ciclos-comerciais", label: "Ciclos Comerciais", icon: CalendarClock },
    { path: "/configuracoes-pagamento", label: "Configurações de Pagamento", icon: CreditCard },
    // Carga única dos grupos do Magento; o cadastro do dia a dia fica em Backoffice > Clientes > Grupos de cliente.
    ...(isCustomerGroupAddressEnabled
      ? [{ path: "/importar-grupos-cliente", label: "Importar Grupos de Cliente", icon: UsersRound }]
      : []),
    ...(isNewScheduledOrderCycleEnabled
      ? [
        { path: "/politicas-corte", label: "Políticas de Corte", icon: ShieldCheck },
        { path: "/produtos-programados", label: "Produtos Programados", icon: PackageCheck },
        ...(isExamShipmentDistributionEnabled
          ? [{ path: "/distribuicao-remessas", label: "Distribuição de Remessas", icon: Truck }]
          : []),
      ]
      : []),
    ...(isIntegratedOrdersEnabled
      ? [{ path: "/pedidos-integrados", label: "Pedidos integrados", icon: PlugZap }]
      : []),
    ...(isInvoiceReturnsEnabled
      ? [{ path: "/retornos-erp", label: "Retornos do ERP", icon: Inbox }]
      : []),
  ];

  const developmentItems = canOverrideDynamicsEnvironment
    ? [
      { path: "/configuracoes-sessao", label: "Configurações da Sessão", icon: Settings },
      { path: "/diagnostico-apis", label: "Diagnóstico de APIs", icon: ServerCog },
      ...(isNewOrderZendeskTicketEnabled
        ? [{ path: "/chamados-zendesk", label: "Chamados no Zendesk", icon: Ticket }]
        : []),
    ]
    : [];

  const navigationSections = [
    { title: "Administrativo", items: administrativeItems },
    ...(developmentItems.length > 0
      ? [{ title: "Desenvolvimento", items: developmentItems }]
      : []),
  ];

  const navigationItems = navigationSections.flatMap((section) => section.items);

  const isAdminPage = navigationItems.some((item) => location.pathname.startsWith(item.path))
    || location.pathname.startsWith("/cadastro-produto")
    || location.pathname.startsWith("/cadastro-cliente");

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background text-foreground">
      {isAdminPage && (
        <aside
          className={cn(
            "flex h-full shrink-0 flex-col border-r bg-card transition-[width] duration-300",
            collapsed ? "w-16" : "w-64",
          )}
        >
          <div className="flex h-16 shrink-0 items-center justify-between border-b px-4">
            {!collapsed && (
              <Link to="/" className="flex min-w-0 items-center gap-2" aria-label="Ir para o início">
                <div className="rounded-lg bg-primary p-1.5">
                  <Package className="h-5 w-5 text-primary-foreground" />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-bold text-primary">Portal de Pedidos</p>
                  <p className="truncate text-xs text-muted-foreground">Painel Administrativo</p>
                </div>
              </Link>
            )}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className={cn("shrink-0", collapsed && "mx-auto")}
              onClick={() => setCollapsed((current) => !current)}
              aria-label={collapsed ? "Expandir menu lateral" : "Recolher menu lateral"}
              aria-expanded={!collapsed}
              aria-controls="admin-sidebar-navigation"
            >
              {collapsed ? <Menu className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
            </Button>
          </div>

          <nav
            id="admin-sidebar-navigation"
            aria-label="Navegação do Painel Administrativo"
            className="min-h-0 flex-1 overflow-y-auto p-3"
          >
            <div className="space-y-4">
              {navigationSections.map((section) => (
                <section key={section.title} aria-labelledby={`admin-section-${section.title.toLowerCase()}`}>
                  {!collapsed && (
                    <h2
                      id={`admin-section-${section.title.toLowerCase()}`}
                      className="mb-2 px-3 text-xs font-bold uppercase tracking-wider text-muted-foreground"
                    >
                      {section.title}
                    </h2>
                  )}
                  <ul className="m-0 space-y-1 p-0">
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname.startsWith(item.path);

                      return (
                        <li key={item.path}>
                          <Link
                            to={item.path}
                            title={collapsed ? item.label : undefined}
                            aria-current={isActive ? "page" : undefined}
                            className={cn(
                              "flex h-10 items-center gap-3 rounded-md px-3 text-sm font-medium transition-colors",
                              collapsed && "justify-center px-0",
                              isActive
                                ? "bg-primary text-primary-foreground shadow-sm"
                                : "text-foreground hover:bg-accent",
                            )}
                          >
                            <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                            {!collapsed && <span className="truncate">{item.label}</span>}
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </div>
          </nav>
        </aside>
      )}

      <div className="min-w-0 flex-1 overflow-y-auto">
        <main className="mx-auto w-full max-w-[1600px] px-4 py-6 sm:px-6 lg:px-8">
          <div className="mb-6">
            <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
            {isAdminPage && <p className="text-sm text-muted-foreground">Painel Administrativo</p>}
          </div>
          {children}
        </main>
      </div>
    </div>
  );
}
